import { apiGet, apiPatch } from './client';
import type {
  RateHistory,
  RateHistoryBatchRequest,
  RateHistoryKey,
} from '../types/RateHistory';

// Composite-PK recipe (mirrors calcMethodMap.ts) — synthesize idKey on read,
// parse it back to a {assetId, dDate} pair on write.
function withIdKey(rows: Omit<RateHistory, 'idKey'>[]): RateHistory[] {
  return rows.map((r) => ({ ...r, idKey: `${r.assetId}|${r.dDate}` }));
}

function stripIdKey(
  rows: RateHistory[],
): Omit<RateHistory, 'idKey'>[] {
  return rows.map(({ idKey, ...rest }) => rest);
}

function parseIdKey(idKey: string): RateHistoryKey {
  const [a, d] = idKey.split('|');
  return { assetId: Number(a), dDate: d };
}

function buildQuery(fromDate?: string, toDate?: string): string {
  const params = new URLSearchParams();
  if (fromDate) params.set('fromDate', fromDate);
  if (toDate) params.set('toDate', toDate);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export async function getRatesHistory(
  fromDate?: string,
  toDate?: string,
): Promise<RateHistory[]> {
  const rows = await apiGet<Omit<RateHistory, 'idKey'>[]>(
    `/api/rates-history${buildQuery(fromDate, toDate)}`,
  );
  return withIdKey(rows);
}

export async function saveRatesHistoryBatch(
  added: RateHistory[],
  modified: RateHistory[],
  deletedIdKeys: string[],
  fromDate?: string,
  toDate?: string,
): Promise<RateHistory[]> {
  const body: RateHistoryBatchRequest = {
    added: stripIdKey(added),
    modified: stripIdKey(modified),
    deleted: deletedIdKeys.map(parseIdKey),
    fromDate,
    toDate,
  };
  const fresh = await apiPatch<
    RateHistoryBatchRequest,
    Omit<RateHistory, 'idKey'>[]
  >('/api/rates-history/batch', body);
  return withIdKey(fresh);
}
